// Ad-platform design tokens + small UI atoms (Module K visual foundation).
//
// Brand-neutral "dark teal-mint" design language: dark surfaces, a teal-mint
// gradient (#00d4aa -> #2dffc4), subtle radial glows. Every Module K UI
// surface (charts, routes, auth theming) and Module O domain UI pulls from
// these tokens so the whole ad UI shares one consistent look — no per-page
// hardcoded hex values outside this file.
//
// Inline styles, not a CSS framework, to match the reference dashboard and
// keep this a faithful, low-risk port. Atoms are authored with
// `createElement` because this is a `.ts` file (no JSX), keeping the public
// filename `theme.ts` exactly as the consumers import it.

import {
  type CSSProperties,
  createElement,
  Fragment,
  type ReactElement,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

export const tokens = {
  color: {
    bg0: '#08080d',
    bg1: '#0c0c15',
    bg2: '#14141d',
    bg3: '#1a1a28',
    bg4: '#1e1e30',
    fg0: '#eeeef2',
    fg1: '#9494a8',
    fg2: '#555568',
    teal: '#00d4aa',
    tealLight: '#2dffc4',
    tealDeep: '#009b7d',
    danger: '#fca5a5',
    dangerBg: 'rgba(220, 38, 38, 0.10)',
    dangerBorder: 'rgba(220, 38, 38, 0.4)',
    warning: '#d4a853',
    border: 'rgba(255,255,255,0.06)',
    borderMid: 'rgba(255,255,255,0.10)',
    borderStrong: 'rgba(255,255,255,0.16)',
    glow: 'rgba(0,212,170,0.06)',
  },
  gradient: {
    brand: 'linear-gradient(135deg,#2dffc4,#00d4aa)',
    brandText: 'linear-gradient(90deg, #2dffc4, #00d4aa)',
    pageBg:
      'radial-gradient(circle at 20% 0%, rgba(0,212,170,0.08) 0%, transparent 45%),' +
      'radial-gradient(circle at 80% 100%, rgba(0,212,170,0.05) 0%, transparent 45%),' +
      '#08080d',
  },
  radius: {
    sm: 6,
    md: '10px',
    lg: 16,
    pill: 999,
  },
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  shadow: {
    card: '0 12px 32px rgba(0,0,0,0.30), 0 0 0 1px rgba(0,212,170,0.04)',
    elevated: '0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,212,170,0.06)',
    pillBrand: '0 6px 20px rgba(0,212,170,0.25)',
  },
  font: {
    family:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, "Roboto Mono", monospace',
    size: {
      xs: 11,
      sm: 12,
      md: 13,
      base: 14,
      lg: 16,
      xl: 20,
      xxl: 26,
      hero: 32,
    },
  },
  breakpoint: {
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1280,
  },
} as const;

// Convenience re-exports so consumers can `import { gradient, radius } ...`
// without reaching through `tokens.*` (mirrors how the reference atoms read).
export const gradient = tokens.gradient;
export const radius = tokens.radius;
export const space = tokens.space;
export const shadow = tokens.shadow;
export const font = tokens.font;

export const chartPalette = [
  tokens.color.tealLight,
  tokens.color.teal,
  tokens.color.warning,
  tokens.color.danger,
  '#a78bfa',
  '#60a5fa',
];

export const chartTheme = {
  grid: tokens.color.border,
  axis: tokens.color.fg2,
  axisText: tokens.color.fg1,
  tooltipBg: tokens.color.bg2,
  tooltipBorder: tokens.color.borderMid,
  tooltipText: tokens.color.fg0,
};

// ---------------------------------------------------------------------------
// Atom: Card
// ---------------------------------------------------------------------------

export function Card({
  title,
  action,
  children,
  padding = tokens.space.lg,
  style,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padding?: number | string;
  style?: CSSProperties;
}): ReactElement {
  return createElement(
    'section',
    {
      style: {
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        boxShadow: tokens.shadow.card,
        padding,
        ...style,
      },
    },
    title || action
      ? createElement(
          'header',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: tokens.space.md,
              marginBottom: tokens.space.lg,
            },
          },
          typeof title === 'string'
            ? createElement(
                'h2',
                {
                  style: {
                    margin: 0,
                    fontSize: tokens.font.size.lg,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: tokens.color.fg0,
                  },
                },
                title,
              )
            : title,
          action ?? null,
        )
      : null,
    children,
  );
}

// ---------------------------------------------------------------------------
// Atom: StatCard — large KPI tile
// ---------------------------------------------------------------------------

export function StatCard({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}): ReactElement {
  return createElement(
    'div',
    {
      style: {
        background: tokens.color.bg2,
        border: `1px solid ${emphasis ? 'rgba(0,212,170,0.28)' : tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
        boxShadow: tokens.shadow.card,
        position: 'relative',
        overflow: 'hidden',
      },
    },
    emphasis
      ? createElement('div', {
          'aria-hidden': true,
          style: {
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 0% 0%, rgba(0,212,170,0.18) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        })
      : null,
    createElement(
      'div',
      {
        style: {
          fontSize: tokens.font.size.xs,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: tokens.color.fg1,
          fontWeight: 600,
          position: 'relative',
        },
      },
      label,
    ),
    createElement(
      'div',
      {
        style: {
          marginTop: tokens.space.sm,
          fontSize: tokens.font.size.hero,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          fontFamily: tokens.font.mono,
          fontVariantNumeric: 'tabular-nums',
          color: tokens.color.fg0,
          lineHeight: 1.1,
          position: 'relative',
        },
      },
      value,
    ),
    hint
      ? createElement(
          'div',
          {
            style: {
              marginTop: tokens.space.xs,
              fontSize: tokens.font.size.sm,
              color: tokens.color.fg1,
              position: 'relative',
            },
          },
          hint,
        )
      : null,
  );
}

// ---------------------------------------------------------------------------
// Atom: Pill — used for status indicators (active/inactive, etc.)
//
// `tone="success"` is the green-tick "Active" indicator reused by Module O for
// domain status: teal bg rgba(0,212,170,0.10), border rgba(0,212,170,0.30),
// color #2dffc4.
// ---------------------------------------------------------------------------

export type PillTone = 'neutral' | 'success' | 'warning' | 'danger';

export function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: PillTone;
  children: ReactNode;
}): ReactElement {
  const palette = {
    neutral: {
      bg: 'rgba(255,255,255,0.06)',
      border: tokens.color.borderMid,
      color: tokens.color.fg1,
    },
    success: {
      bg: 'rgba(0,212,170,0.10)',
      border: 'rgba(0,212,170,0.30)',
      color: tokens.color.tealLight,
    },
    warning: {
      bg: 'rgba(212,168,83,0.12)',
      border: 'rgba(212,168,83,0.40)',
      color: tokens.color.warning,
    },
    danger: {
      bg: tokens.color.dangerBg,
      border: tokens.color.dangerBorder,
      color: tokens.color.danger,
    },
  }[tone];
  return createElement(
    'span',
    {
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: tokens.radius.pill,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontSize: tokens.font.size.xs,
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
      },
    },
    children,
  );
}

// ---------------------------------------------------------------------------
// Atom: Button — primary / ghost / subtle / danger variants, all pill-shaped
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'ghost' | 'subtle' | 'danger';

const buttonBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: tokens.radius.pill,
  fontSize: tokens.font.size.md,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid transparent',
  transition: 'transform 120ms ease, filter 120ms ease, box-shadow 120ms ease',
};

export function buttonStyle(variant: ButtonVariant = 'primary', disabled = false): CSSProperties {
  const variants: Record<ButtonVariant, CSSProperties> = {
    primary: {
      background: tokens.gradient.brand,
      color: tokens.color.bg0,
      boxShadow: tokens.shadow.pillBrand,
    },
    ghost: {
      background: 'transparent',
      color: tokens.color.fg1,
      border: `1px solid ${tokens.color.borderMid}`,
    },
    subtle: {
      background: 'rgba(0,212,170,0.08)',
      color: tokens.color.tealLight,
      border: '1px solid rgba(0,212,170,0.30)',
    },
    danger: {
      background: tokens.color.dangerBg,
      color: tokens.color.danger,
      border: `1px solid ${tokens.color.dangerBorder}`,
    },
  };
  return {
    ...buttonBase,
    ...variants[variant],
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' } : {}),
  };
}

// ---------------------------------------------------------------------------
// Atom: Field + Input — themed form controls
// ---------------------------------------------------------------------------

export function FieldLabel({
  htmlFor,
  children,
  hint,
}: {
  htmlFor?: string;
  children: ReactNode;
  hint?: string;
}): ReactElement {
  return createElement(
    'label',
    {
      htmlFor,
      style: { display: 'block', marginBottom: tokens.space.md },
    },
    createElement(
      'div',
      {
        style: {
          fontSize: tokens.font.size.xs,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        },
      },
      children,
    ),
    hint
      ? createElement(
          'div',
          {
            style: {
              fontSize: tokens.font.size.xs,
              color: tokens.color.fg2,
              marginBottom: tokens.space.xs,
            },
          },
          hint,
        )
      : null,
  );
}

export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.borderMid}`,
  background: tokens.color.bg1,
  color: tokens.color.fg0,
  fontSize: tokens.font.size.base,
  fontFamily: tokens.font.family,
  outline: 'none',
};

// ---------------------------------------------------------------------------
// Atom: PageHeader — consistent page-level title row
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}): ReactElement {
  return createElement(
    'header',
    {
      style: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: tokens.space.lg,
        marginBottom: tokens.space.xl,
      },
    },
    createElement(
      'div',
      null,
      createElement(
        'h1',
        {
          style: {
            margin: 0,
            fontSize: tokens.font.size.xxl,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: tokens.color.fg0,
          },
        },
        title,
      ),
      subtitle
        ? createElement(
            'p',
            {
              style: {
                margin: `${tokens.space.xs}px 0 0`,
                color: tokens.color.fg1,
                fontSize: tokens.font.size.base,
              },
            },
            subtitle,
          )
        : null,
    ),
    actions
      ? createElement('div', { style: { display: 'flex', gap: tokens.space.sm } }, actions)
      : null,
  );
}

// ---------------------------------------------------------------------------
// Atom: EmptyState — used when a chart or list has no data yet
// ---------------------------------------------------------------------------

export function EmptyState({
  title,
  hint,
  icon,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
}): ReactElement {
  return createElement(
    'div',
    {
      style: {
        padding: tokens.space.xxl,
        textAlign: 'center',
        color: tokens.color.fg1,
        fontSize: tokens.font.size.base,
      },
    },
    icon
      ? createElement(
          'div',
          { style: { marginBottom: tokens.space.sm, color: tokens.color.fg2 } },
          icon,
        )
      : null,
    createElement('div', { style: { color: tokens.color.fg0, fontWeight: 600 } }, title),
    hint ? createElement('div', { style: { marginTop: tokens.space.xs } }, hint) : null,
  );
}

// ---------------------------------------------------------------------------
// Atom: Skeleton — neutral loading bar (animates against the `shimmer`
// keyframe defined in apps/web/src/index.css).
// ---------------------------------------------------------------------------

export function Skeleton({
  height = 14,
  width = '100%',
  radius: cornerRadius = tokens.radius.sm,
}: {
  height?: number;
  width?: number | string;
  radius?: number;
}): ReactElement {
  return createElement('div', {
    style: {
      height,
      width,
      borderRadius: cornerRadius,
      background:
        'linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s ease-in-out infinite',
    },
  });
}

// Re-exported so consumers that need to compose multiple atoms without an
// extra wrapper element can use `createElement(Fragment, ...)`.
export { Fragment };

// ---------------------------------------------------------------------------
// Tooltip helpers for Recharts (themed) — consumed by K2's chart wrappers.
// ---------------------------------------------------------------------------

export const tooltipContentStyle: CSSProperties = {
  background: chartTheme.tooltipBg,
  border: `1px solid ${chartTheme.tooltipBorder}`,
  borderRadius: tokens.radius.sm,
  padding: '8px 10px',
  fontSize: tokens.font.size.sm,
  color: chartTheme.tooltipText,
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
  fontFamily: tokens.font.family,
};

export const tooltipLabelStyle: CSSProperties = {
  color: tokens.color.fg1,
  fontSize: tokens.font.size.xs,
  marginBottom: 4,
};

export const tooltipItemStyle: CSSProperties = {
  color: tokens.color.fg0,
  fontFamily: tokens.font.mono,
  fontVariantNumeric: 'tabular-nums',
};
